import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  assignTest, errorDetail, fetchAssignments, fetchEmployees, fetchOverviewDetail, humanError,
} from '../api';
import { Icon } from '../components/icons';
import { WorkspaceShell } from '../components/WorkspaceShell';
import { withAdminOnly } from '../components/withAdminOnly';
import { useFocusOnShow } from '../hooks';
import { ASSIGNMENT_STATE_LABEL, parseServerDate } from '../model';
import type { AssignmentRow, Employee, OverviewDetail } from '../model';
import { pushNotice, takeNotice } from '../notices';
import type { WorkspaceNotice } from '../notices';
import { toggleId } from '../selectionStore';
import { num, peopleLabel } from '../words';

interface Conflict {
  user_id: number;
  user_name: string | null;
  test_title?: string;
  overlap_percent?: number;
  completed_at?: string | null;
}

interface Pending {
  kind: 'retake' | 'similar';
  conflicts: Conflict[];
}

const warningOf = (detail: OverviewDetail): string => {
  if (detail.status === 'public') {
    return 'Сейчас этот тест доступен всем. Когда вы дадите его конкретным людям, остальные сотрудники перестанут его видеть.';
  }
  if (detail.status === 'assigned') {
    return `Тест уже выдан: ${peopleLabel(detail.issued_count)}. Отметьте тех, кому нужно дать его ещё.`;
  }
  return 'Сейчас этот тест не видит никто. После выдачи его увидят только отмеченные люди.';
};

const two = (value: number): string => String(value).padStart(2, '0');

const todayLocal = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${two(now.getMonth() + 1)}-${two(now.getDate())}`;
};

/** Срок — конец выбранного дня; так же задаёт срок доска назначений. */
const dueAtOf = (date: string): string | null => {
  if (!date) return null;
  const end = new Date(`${date}T23:59:59`);
  return Number.isNaN(end.getTime()) ? null : end.toISOString();
};

const dayOf = (iso: string | null | undefined): string => {
  const date = parseServerDate(iso); // время сервера без пояса — UTC; день называем местный
  return date ? `${two(date.getDate())}.${two(date.getMonth() + 1)}.${date.getFullYear()}` : '';
};

/** « (последний раз — 01.09.2026)»; без даты пусто: подпись не должна кончаться пустым хвостом «(последний раз — )». */
const lastTimeTail = (iso: string | null | undefined): string => {
  const day = dayOf(iso);
  return day ? ` (последний раз — ${day})` : '';
};

const conflictsOf = (info: Record<string, unknown>): Conflict[] => (
  Array.isArray(info.conflicts) ? (info.conflicts as Conflict[]) : []
);

const TestAssignPage: React.FC = () => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const testId = Number(useParams().testId);

  const [state, setState] = useState<'loading' | 'ready' | 'failed' | 'missing'>('loading');
  const [detail, setDetail] = useState<OverviewDetail | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<number[]>([]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [due, setDue] = useState('');
  const [notice, setNotice] = useState<WorkspaceNotice | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const requestRef = useRef(0);
  const allowRef = useRef({ retake: false, similar: false }); // согласия на 409; на новую попытку не переносятся
  // Вопрос подтверждения заменяет экран, нажатая кнопка исчезает: фокус переходит на заголовок вопроса.
  const pendingHeading = useFocusOnShow<HTMLHeadingElement>(pending !== null);

  useEffect(() => {
    // Не в инициализаторе useState: под <React.StrictMode> компонент рендерится дважды, второй вызов вернул бы null.
    const incoming = takeNotice();
    if (incoming) setNotice(incoming);
  }, []);

  const load = useCallback(async () => {
    const request = ++requestRef.current; // устаревший ответ не должен затереть свежий
    if (!Number.isInteger(testId) || testId < 1) {
      setState('missing'); // /admin/tests/abc/assign: запрос с NaN сервер отклонил бы (422)
      return;
    }
    setState('loading');
    try {
      const [info, people, rows] = await Promise.all([
        fetchOverviewDetail(testId),
        fetchEmployees(),
        fetchAssignments(testId),
      ]);
      if (request !== requestRef.current) return;
      if (info.status === 'archived') {
        pushNotice({ kind: 'info', text: 'Тест в архиве. Сначала верните его из архива.' });
        navigateRef.current(`/admin/tests/${testId}`, { replace: true });
        return;
      }
      setDetail(info);
      setEmployees(people);
      setAssignments(rows);
      setState('ready');
    } catch (error) {
      if (request !== requestRef.current) return;
      const status = (error as { response?: { status?: number } } | null)?.response?.status;
      setState(status === 404 ? 'missing' : 'failed');
    }
  }, [testId]);

  useEffect(() => {
    void load();
  }, [load]);

  const existing = new Map(assignments.map((row): [number, AssignmentRow] => [row.user_id, row]));
  const needle = query.trim().toLowerCase();
  const shown = employees.filter(
    (person) => !needle
      || [person.full_name, person.position, person.department].filter(Boolean).join(' ').toLowerCase().includes(needle),
  );
  const fresh = picked.filter((id) => !existing.has(id));
  const duePast = due !== '' && due < todayLocal();
  const dueBad = due !== '' && dueAtOf(due) === null; // поле даты принимает и год из пяти цифр, а разобрать его нельзя
  const dueProblem = duePast || dueBad;
  const back = () => navigate(`/admin/tests/${testId}`);

  const toggle = (id: number) => {
    allowRef.current = { retake: false, similar: false };
    setPicked((current) => toggleId(current, id));
  };

  const submit = async () => {
    if (busyRef.current || fresh.length === 0 || dueProblem) return;
    setNotice(null);
    busyRef.current = true;
    setBusy(true);
    try {
      await assignTest({
        test_id: testId,
        user_ids: fresh,
        due_at: dueAtOf(due),
        allow_retake: allowRef.current.retake,
        allow_similar_test: allowRef.current.similar,
      });
      pushNotice({ kind: 'ok', text: `Тест выдан: ${peopleLabel(fresh.length)}. Теперь его видят только те, кому он выдан.` });
      navigate(`/admin/tests/${testId}?tab=given`);
    } catch (error) {
      const info = errorDetail(error);
      if (info?.code === 'retake_confirmation_required') {
        setPending({ kind: 'retake', conflicts: conflictsOf(info) });
      } else if (info?.code === 'similar_test_confirmation_required') {
        setPending({ kind: 'similar', conflicts: conflictsOf(info) });
      } else {
        allowRef.current = { retake: false, similar: false }; // согласия относились к прошлой попытке
        const status = (error as { response?: { status?: number } } | null)?.response?.status;
        setNotice({
          kind: 'info',
          // слова сервера при 404 — «Пользователи не найдены: [12]»: людям они не нужны
          text: status === 404
            ? 'Теста или кого-то из отмеченных сотрудников уже нет. Обновите страницу и попробуйте ещё раз.'
            : humanError(error, 'Не получилось дать тест. Ничего не изменилось: попробуйте ещё раз.'),
        });
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const confirmPending = () => {
    if (!pending) return;
    allowRef.current = { ...allowRef.current, [pending.kind === 'retake' ? 'retake' : 'similar']: true };
    setPending(null);
    void submit();
  };
  const declinePending = () => {
    allowRef.current = { retake: false, similar: false };
    setPending(null);
  };

  if (state !== 'ready' || detail === null) {
    return (
      <WorkspaceShell notice={notice} onCloseNotice={() => setNotice(null)}>
        {state === 'loading' ? (
          <div className="tw-card tw-empty" aria-busy="true"><p className="tw-help">Загружаем данные…</p></div>
        ) : (
          <div className="tw-card tw-empty">
            <Icon name="info" size={44} large />
            <h1>{state === 'missing' ? 'Такого теста нет' : 'Не получилось загрузить данные'}</h1>
            <p className="tw-help">
              {state === 'missing' ? 'Возможно, его уже убрали. Вернитесь к списку тестов.' : 'Проверьте соединение и попробуйте ещё раз.'}
            </p>
            {state === 'failed' && <button type="button" className="tw-btn tw-btn-primary" onClick={() => void load()}>Повторить</button>}
            <button type="button" className="tw-btn" onClick={() => navigate('/admin/tests')}>Все тесты</button>
          </div>
        )}
      </WorkspaceShell>
    );
  }

  const heading = (
    <>
      <div className="tw-row">
        <button type="button" className="tw-btn tw-btn-quiet" onClick={back}>
          <Icon name="left" />
          Назад к тесту
        </button>
      </div>
      <div>
        <h1>{`Кому дать тест «${detail.title}»?`}</h1>
        <p className="tw-lead" style={{ marginTop: 8 }}>Отметьте сотрудников галочкой. Они увидят тест у себя и смогут его пройти.</p>
      </div>
    </>
  );

  if (detail.questions_count === 0) {
    return (
      <WorkspaceShell notice={notice} onCloseNotice={() => setNotice(null)}>
        <section className="tw-screen">
          {heading}
          <div className="tw-card tw-empty">
            <Icon name="info" size={44} large />
            <h2>В тесте пока нет вопросов</h2>
            <p className="tw-help">Тест без вопросов нельзя дать сотрудникам. Сначала добавьте вопросы.</p>
            <button type="button" className="tw-btn tw-btn-primary" onClick={() => navigate(`/admin/tests/${testId}/questions/add`)}>
              Добавить вопросы
            </button>
          </div>
        </section>
      </WorkspaceShell>
    );
  }

  if (pending) {
    const retake = pending.kind === 'retake';
    return (
      <WorkspaceShell notice={notice} onCloseNotice={() => setNotice(null)}>
        <section className="tw-screen">
          {heading}
          <div className="tw-panel tw-stack-sm">
            <h2 ref={pendingHeading} tabIndex={-1}>{retake ? 'Эти сотрудники уже проходили тест' : 'Похожий тест уже выдан'}</h2>
            <p className="tw-help">
              {retake
                ? 'Если дать им тест снова, они смогут пройти его ещё раз.'
                : 'У этих сотрудников уже есть тест с почти такими же вопросами. Если дать и этот, им придётся отвечать на одно и то же.'}
            </p>
            <ul className="tw-olist">
              {pending.conflicts.map((conflict, index) => (
                <li key={`${index}-${conflict.user_id}`}>
                  {retake
                    ? `${conflict.user_name ?? 'Сотрудник'}${lastTimeTail(conflict.completed_at)}`
                    : `${conflict.user_name ?? 'Сотрудник'}: «${conflict.test_title ?? 'другой тест'}» — совпадает на ${Math.round(conflict.overlap_percent ?? 0)}%`}
                </li>
              ))}
            </ul>
            <div className="tw-row tw-btn-row">
              <button type="button" className="tw-btn tw-btn-primary" onClick={confirmPending}>
                {retake ? 'Да, дать ещё раз' : 'Всё равно дать'}
              </button>
              <button type="button" className="tw-btn" onClick={declinePending}>Не давать</button>
            </div>
          </div>
        </section>
      </WorkspaceShell>
    );
  }

  const basket = (
    <div className="tw-basket">
      <div className="tw-basket-in">
        <span className="tw-basket-count">
          {fresh.length > 0 ? `Выбрано: ${num(fresh.length, 'сотрудник', 'сотрудника', 'сотрудников')}` : 'Пока никого не выбрали'}
        </span>
        <span className="tw-basket-actions">
          <button type="button" className="tw-btn" onClick={back}>Отмена</button>
          <button type="button" className="tw-btn tw-btn-primary" data-guide="assignment-confirm" disabled={fresh.length === 0 || busy || dueProblem} onClick={() => void submit()}>
            <Icon name="check" />
            Дать тест
          </button>
        </span>
        {fresh.length === 0 && <span className="tw-help tw-basket-hint">Отметьте галочкой хотя бы одного сотрудника.</span>}
        {fresh.length > 0 && dueProblem && !moreOpen && (
          <span className="tw-help tw-basket-hint">
            {dueBad
              ? 'Срок указан неверно. Откройте «Дополнительно» и поправьте дату.'
              : 'Срок уже прошёл. Откройте «Дополнительно» и выберите другую дату.'}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <WorkspaceShell notice={notice} onCloseNotice={() => setNotice(null)} basket={basket}>
      <section data-guide="assignment-form" className="tw-screen">
        {heading}
        <div className="tw-warn">
          <Icon name="info" />
          <span>{warningOf(detail)}</span>
        </div>

        <div className="tw-card tw-stack-sm">
          <div>
            <label className="tw-label" htmlFor="tw-aq">Найти сотрудника</label>
            <div className="tw-search">
              <Icon name="search" />
              <input
                data-guide="assignment-search" id="tw-aq"
                className="tw-field"
                type="text"
                placeholder="Например: Анна"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          </div>
          {shown.length > 0 && <p className="tw-help">{`Найдено сотрудников: ${shown.length}`}</p>}
          {assignments.length > 0 && (
            <p className="tw-help">
              Тех, кому тест уже выдан, отметить нельзя. Повторная выдача, продление и пересдача — на{' '}
              <Link to={`/admin/board?test=${testId}`}>доске назначений</Link>.
            </p>
          )}
          <div>
            <button type="button" className="tw-btn tw-btn-quiet" data-guide="assignment-more" aria-expanded={moreOpen} onClick={() => setMoreOpen(!moreOpen)}>
              Дополнительно — можно пропустить
              <Icon name="down" />
            </button>
            {moreOpen && (
              <div className="tw-panel tw-stack-sm" style={{ marginTop: 12 }}>
                <div>
                  <label className="tw-label" htmlFor="tw-due">Срок, до которого нужно пройти тест (необязательно)</label>
                  <input data-guide="assignment-due" id="tw-due" className="tw-field" type="date" min={todayLocal()} value={due} onChange={(event) => setDue(event.target.value)} />
                  <p className="tw-help" style={{ marginTop: 8 }}>Без срока тест можно пройти в любое время.</p>
                  {dueBad ? (
                    <p className="tw-help">Такой даты нет. Проверьте год, месяц и число.</p>
                  ) : (
                    duePast && <p className="tw-help">Эта дата уже прошла. Выберите сегодняшнюю или более позднюю.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <ul data-guide="assignment-recipients" className="tw-qlist">
          {shown.map((person) => {
            const row = existing.get(person.id);
            const checked = !row && picked.includes(person.id);
            const role = [person.position, person.department].filter(Boolean).join(' · ');
            return (
              <li key={person.id}>
                <label className={`tw-pick${checked ? ' tw-pick-on' : ''}${row ? ' tw-pick-off' : ''}`}>
                  <input type="checkbox" checked={checked} disabled={Boolean(row) || busy} onChange={() => toggle(person.id)} />
                  <span className="tw-pick-body">
                    <span className="tw-pick-title">{person.full_name}</span>
                    {role && <span className="tw-pick-text">{role}</span>}
                    {row && (
                      <>
                        <span className="tw-pill tw-pill-given">Уже выдан</span>
                        <span className="tw-pick-text">{ASSIGNMENT_STATE_LABEL[row.effective_state]}</span>
                      </>
                    )}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>

        {employees.length > 0 && shown.length === 0 && (
          <div className="tw-card tw-empty">
            <Icon name="search" size={44} large />
            <h2>Такого сотрудника нет</h2>
            <p className="tw-help">Проверьте, как написано имя, или покажите всех сотрудников.</p>
            <button type="button" className="tw-btn tw-btn-primary" onClick={() => setQuery('')}>Показать всех</button>
          </div>
        )}
        {employees.length === 0 && (
          <div className="tw-card tw-empty">
            <Icon name="users" size={44} large />
            <h2>Пока нет сотрудников, которым можно дать тест</h2>
            <p className="tw-help">Добавьте сотрудников в разделе «Пользователи».</p>
          </div>
        )}
      </section>
    </WorkspaceShell>
  );
};

export default withAdminOnly(TestAssignPage);
